/**
 * @description
 * Deployment service for managing deployment operations and lifecycle.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { Deployment, CreateDeploymentRequest } from "./types";

/**
 * Deployment service for managing deployments
 */
export class DeploymentService extends BaseAPIClient {
  /**
   * Get all deployments
   */
  async getDeployments(): Promise<Deployment[]> {
    const data = await this.request<{ deployments: Deployment[] }>(
      "/deployments",
    );
    return data.deployments;
  }

  /**
   * Get deployments for a specific client
   */
  async getClientDeployments(clientId: string): Promise<Deployment[]> {
    const data = await this.request<{ deployments: Deployment[] }>(
      `/deployments?clientId=${clientId}`,
    );
    return data.deployments;
  }

  /**
   * Create a new deployment
   */
  async createDeployment(
    clientId: string,
    data?: CreateDeploymentRequest,
  ): Promise<Deployment> {
    const response = await this.request<{ deployment: Deployment }>(
      "/deployments",
      {
        method: "POST",
        body: JSON.stringify({ clientId, ...data }),
      },
    );
    return response.deployment;
  }

  /**
   * Get deployment status
   */
  async getDeploymentStatus(deploymentId: string): Promise<Deployment> {
    const data = await this.request<{ deployment: Deployment }>(
      `/deployments/${deploymentId}`,
    );
    return data.deployment;
  }

  /**
   * Rollback a deployment
   */
  async rollbackDeployment(deploymentId: string): Promise<{
    success: boolean;
    message: string;
    details: {
      deleted: string[];
      failed: string[];
      orphaned: string[];
    };
  }> {
    const response = await this.request<{
      success: boolean;
      message: string;
      details: {
        deleted: string[];
        failed: string[];
        orphaned: string[];
      };
    }>(`/deployments/${deploymentId}/rollback`, {
      method: 'POST',
    });
    return response;
  }

  /**
   * Delete a deployment
   */
  async deleteDeployment(deploymentId: string): Promise<void> {
    await this.request(`/deployments/${deploymentId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get deployment details
   */
  async getDeploymentDetails(deploymentId: string): Promise<{
    id: string;
    clientId: string;
    version: string;
    status: 'pending' | 'deploying' | 'success' | 'failed' | 'rolled_back';
    deployedBy: string;
    startedAt: string;
    completedAt: string | null;
    errorMessage: string | null;
    logs: string;
    progress: string;
    clientName?: string;
    subdomain?: string;
  }> {
    const response = await this.request<{
      id: string;
      clientId: string;
      version: string;
      status: 'pending' | 'deploying' | 'success' | 'failed' | 'rolled_back';
      deployedBy: string;
      startedAt: string;
      completedAt: string | null;
      errorMessage: string | null;
      logs: string;
      progress: string;
      clientName?: string;
      subdomain?: string;
    }>(`/deployments/${deploymentId}`, {
      method: 'GET',
    });
    return response;
  }

  /**
   * Get deployment readiness for a client
   */
  async getDeploymentReadiness(clientId: string): Promise<{
    readiness: {
      isReady: boolean;
      readinessScore: number;
      missingRequired: string[];
      missingOptional: string[];
      recommendations: string[];
      details: {
        aiProviders: {
          isValid: boolean;
          configuredProviders: string[];
          missingProviders: string[];
          warnings: string[];
        };
        oauth: {
          isValid: boolean;
          googleOAuth: boolean;
          githubOAuth: boolean;
          githubExporter: boolean;
          warnings: string[];
        };
        cloudflare: {
          isValid: boolean;
          hasApiToken: boolean;
          hasAccountId: boolean;
          warnings: string[];
        };
      };
    };
  }> {
    return this.request(`/deployments/client/${clientId}/readiness`);
  }

  /**
   * Initialize default configurations for a client
   */
  async initializeClientDefaults(clientId: string): Promise<{
    success: boolean;
    initialized: number;
    message: string;
  }> {
    return this.request(`/deployments/client/${clientId}/configs/initialize-defaults`, {
      method: 'POST',
    });
  }

  /**
   * Start local deployment for a client
   */
  async startDeployment(clientId: string, mode: 'local' | 'production'): Promise<any> {
    const response = await this.request<any>(`/clients/${clientId}/deploy?mode=${mode}`, {
      method: 'POST'
    });
    return response;
  }

  /**
   * Stop local deployment for a client
   */
  async stopDeployment(clientId: string): Promise<void> {
    await this.request(`/clients/${clientId}/stop-deployment`, {
      method: 'POST'
    });
  }

  /**
   * Get deployment status for a client
   */
  async getClientDeploymentStatus(clientId: string): Promise<any> {
    const response = await this.request<any>(`/clients/${clientId}/deployment-status`);
    return response;
  }

  /**
   * Get deployment logs for a client
   */
  async getClientDeploymentLogs(clientId: string): Promise<string[]> {
    const response = await this.request<{ logs: string[] }>(`/clients/${clientId}/deployment-logs`);
    return response.logs;
  }

  /**
   * Deploy to production (Cloudflare Pages + Workers)
   */
  async deployToProduction(clientId: string): Promise<any> {
    const response = await this.request<any>(`/clients/${clientId}/deploy-production`, {
      method: 'POST'
    });
    return response;
  }

  /**
   * Start local deployment for a client
   */
  async startLocalDeployment(clientId: string): Promise<any> {
    return this.request(`/deployments/${clientId}/start`, { method: 'POST' });
  }

  /**
   * Stop local deployment for a client
   */
  async stopLocalDeployment(clientId: string): Promise<any> {
    return this.request(`/deployments/${clientId}/stop`, { method: 'POST' });
  }

  /**
   * Get production deployment status
   */
  async getProductionStatus(clientId: string): Promise<any> {
    const response = await this.request<any>(`/clients/${clientId}/production-status`);
    return response;
  }
}

// Export singleton instance
export const deploymentService = new DeploymentService();
